import React, { useContext, useEffect, useReducer, useState } from "react";
import { InputGroup, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/esm/Image";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "react-responsive";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Store } from "../Store";
import AlertBox from "../components/AlertBox";
import CategorySampleEditor from "../components/CategorySampleEditor";
import FiliskaPagination from "../components/FiliskaPagination";
import LoadingBox from "../components/LoadingBox";
import MessageBox from "../components/MessageBox";
import useSampleProductsAndCategories from "../hooks/useSampleProductsAndCategories";
import { getError } from "../utils";
import useAxios from "../hooks/useAxios";

const reducer = (state, action) => {
  switch (action.type) {
    case "DELETE_REQUEST":
      return { ...state, loadingDelete: true, successDelete: false };
    case "DELETE_SUCCESS":
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case "DELETE_FAIL":
      return { ...state, loadingDelete: false, successDelete: false };

    case "DELETE_RESET":
      return { ...state, loadingDelete: false, successDelete: false };

    default:
      return state;
  }
};

export default function CategorySampleListScreen() {
  const axios = useAxios();
  const [
    loading,
    errorLoading,
    setLoadProducts,
    setLoadCategories,
    setLoadLeafCategories,
  ] = useSampleProductsAndCategories();

  const [{ loadingDelete, successDelete }, dispatch] = useReducer(reducer, {
    loadingDelete: false,
    successDelete: false,
  });

  const navigate = useNavigate();
  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const { state } = useContext(Store);
  const { userInfo, allCategories: categories } = state;
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const [page, setPage] = useState(sp.get("page") || 1);
  const [pages, setPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [rootCategory, setRootCategory] = useState(null);
  const [categoriesFiltered, setCategoriesFiltered] = useState([]);
  const [type, setType] = useState("temel");

  const PAGE_SIZE = 5;
  useEffect(() => {
    categories && setPages(Math.ceil(categories.length / PAGE_SIZE));
  }, [categories]);

  useEffect(() => {
    const categoriesRe = (
      !rootCategory
        ? categories
        : categories.filter((c) => c._id === rootCategory)[0].categories
    )
      .filter((c) => (type === "temel" ? c.basic : !c.basic))
      .sort((c1, c2) => new Date(c2.createdAt) - new Date(c1.createdAt))
      .filter((p, i) => {
        if ((page - 1) * PAGE_SIZE <= i && i < page * PAGE_SIZE) {
          return true;
        }
        return false;
      });

    setCategoriesFiltered(categoriesRe);
  }, [page, categories, rootCategory, type]);

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: "DELETE_RESET" });
    }
  }, [page, userInfo, successDelete]);

  const deleteHandler = async (category) => {
    if (window.confirm("Silmek istediğinize emın misiniz?")) {
      try {
        await axios.delete(`/api/categorysamples/${category._id}`);
        toast.success("Category deleted successfully");

        setLoadProducts(true);
        setLoadCategories(true);
        setLoadLeafCategories(true);
        dispatch({ type: "DELETE_SUCCESS" });
        setPage(1);
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: "DELETE_FAIL",
        });
      }
    }
  };

  return (
    <div className="small-container">
      <Helmet>
        <title>Kategori Havuzu</title>
      </Helmet>
      {!isTabletOrMobile && (
        <>
          <h1 className="my-3 text-muted">Kategori Havuzu</h1>
          <hr className="hr"></hr>
        </>
      )}
      <div className="hstack gap-3 mb-3">
        <div>
          <InputGroup>
            <Button
              className={
                type === "temel"
                  ? "text-danger border-danger "
                  : "text-primary border-primary"
              }
              variant="light"
              onClick={() => {
                setType("temel");
              }}
            >
              Temel
            </Button>
            &nbsp;
            <Button
              className={
                type === "ileri"
                  ? "text-danger border-danger "
                  : "text-primary border-primary"
              }
              variant="light"
              onClick={() => {
                setType("ileri");
              }}
            >
              İleri
            </Button>
          </InputGroup>
        </div>
        <div className="ms-auto">
          <div>
            <Button
              variant="light"
              className="text-primary border-primary"
              type="button"
              onClick={() => {
                setSelectedCategory(null);
                setShowCategoryEditor(true);
              }}
            >
              <i className="fas fa-plus"></i>
              {!isTabletOrMobile && <>&nbsp;Yeni</>}
            </Button>
          </div>
        </div>
      </div>

      {loading || loadingDelete ? (
        <LoadingBox></LoadingBox>
      ) : errorLoading ? (
        <MessageBox variant="danger">
          {"Kategoriler yüklenirken hata!"}
        </MessageBox>
      ) : categories && categories.length > 0 ? (
        <>
          <table className="table">
            <thead>
              <tr>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {categoriesFiltered.map((category) => (
                <tr key={category._id}>
                  <td>
                    <Link
                      className="textDecorationNone"
                      to={``}
                      onClick={() => {
                        if (category.categories.length > 0) {
                          setRootCategory(category._id);
                        } else {
                          setSelectedCategory(category);
                          setShowCategoryEditor(true);
                        }
                      }}
                    >
                      <Image
                        style={{ float: "left", marginRight: "0.5rem" }}
                        className="small-product-image fitContent border border-secondary"
                        src={category.image}
                        alt={category.name}
                        fluid
                        rounded
                      ></Image>
                    </Link>

                    <Link
                      to=""
                      className="textDecorationNone"
                      onClick={() => {
                        if (category.categories.length > 0) {
                          setRootCategory(category._id);
                        } else {
                          setSelectedCategory(category);
                          setShowCategoryEditor(true);
                        }
                      }}
                    >
                      <span className="text-dark">{category.name}</span>&nbsp;
                    </Link>
                  </td>
                  <td className="text-end" style={{ width: "35%" }}>
                    <Button
                      variant="light"
                      className={"text-success border-success "}
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowCategoryEditor(true);
                      }}
                    >
                      {!isTabletOrMobile && <>Güncelle</>}{" "}
                      <i className="fa fa-edit"></i>
                    </Button>
                    &nbsp;
                    <Button
                      className={"text-danger border-danger "}
                      variant="light"
                      onClick={() => deleteHandler(category)}
                    >
                      {!isTabletOrMobile && <>Sil</>}{" "}
                      <i className="fas fa-trash-alt"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div>
            {rootCategory ? (
              <Button
                variant="light"
                className="border-primary text-primary"
                onClick={() => setRootCategory(null)}
              >
                <i className="fas fa-caret-left"></i>&nbsp; Başa Dön
              </Button>
            ) : null}
            <FiliskaPagination
              leftRightSize={2}
              page={page}
              pages={pages}
              onFirst={() => setPage(1)}
              onLast={() => setPage(pages)}
              onNext={() => {
                if (page < pages) {
                  setPage(page + 1);
                }
              }}
              onPrev={() => {
                if (page > 1) {
                  setPage(page - 1);
                }
              }}
              onItemNext={(p) => setPage(p)}
              onItemPrev={(p) => setPage(p)}
            ></FiliskaPagination>
          </div>
        </>
      ) : (
        <AlertBox primary>Sisteme kayıtlı kategori bulunamadı</AlertBox>
      )}

      <Modal
        backdrop="static"
        show={showCategoryEditor}
        onHide={() => setShowCategoryEditor(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {!selectedCategory ? "Yeni Kategori" : "Kategori Güncelle"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CategorySampleEditor
            category={selectedCategory || {}}
            setShowCategoryEditor={setShowCategoryEditor}
          ></CategorySampleEditor>
        </Modal.Body>
      </Modal>
    </div>
  );
}
